import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import { X402ActivityRow } from "../components/x402/X402ActivityRow";
import { X402ProfileIdentityLinks } from "../components/x402/X402ProfileIdentityLinks";
import { fetchPublicProfile, shortAddr, type PublicIdentityProfile } from "../lib/api";
import {
  baseAddressFromAccountId,
  fetchPublicX402Profile,
  fetchX402LinkedIdentities,
  type X402ActivityKind,
  type X402LinkedIdentity,
  type X402ProfileActivity,
  type X402PublicProfileResponse,
} from "../lib/x402Profile";

type ActivityFilter = "all" | X402ActivityKind;

const FILTERS: Array<{ value: ActivityFilter; label: string }> = [
  { value: "all", label: "All facts" },
  { value: "used", label: "Used" },
  { value: "reviewed", label: "Reviewed" },
  { value: "recognized", label: "Recognized" },
  { value: "paid_settlement", label: "Settled" },
];

export function X402ProfilePage() {
  const { profileId = "" } = useParams();
  const [data, setData] = useState<X402PublicProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [identitiesByAccount, setIdentitiesByAccount] = useState<Record<string, X402LinkedIdentity[]>>({});
  const [identityLookupFailed, setIdentityLookupFailed] = useState(false);
  const [controllerIdentityProfile, setControllerIdentityProfile] = useState<PublicIdentityProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    setLoading(true);
    fetchPublicX402Profile(profileId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError("The public profile could not be loaded. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [profileId]);

  const activity = useMemo(
    () => data?.activity?.filter((fact) => profileActivityMatchesFilter(fact, filter)) ?? [],
    [data?.activity, filter],
  );
  const controller = data?.profile.wallets.find((wallet) => wallet.isController) ?? data?.profile.wallets[0];
  const controllerAddress = controller ? baseAddressFromAccountId(controller.accountId) : null;
  const walletKey = data?.profile.wallets.map((wallet) => wallet.accountId).join(":") ?? "";

  useEffect(() => {
    if (!data?.profile.wallets.length) {
      setIdentitiesByAccount({});
      setIdentityLookupFailed(false);
      return;
    }
    let cancelled = false;
    Promise.allSettled(data.profile.wallets.map(async (wallet) => {
      const address = baseAddressFromAccountId(wallet.accountId);
      return [wallet.accountId, address ? await fetchX402LinkedIdentities(address) : []] as const;
    })).then((results) => {
      if (cancelled) return;
      const entries = results.flatMap((result) => result.status === "fulfilled" ? [result.value] : []);
      setIdentitiesByAccount(Object.fromEntries(entries));
      setIdentityLookupFailed(results.some((result) => result.status === "rejected"));
    });
    return () => { cancelled = true; };
  }, [walletKey]);

  const controllerIdentities = controller ? identitiesByAccount[controller.accountId] ?? [] : [];
  const controllerIdentity = controllerIdentities.find((identity) => identity.verification === "boon_oauth_link") ?? controllerIdentities[0] ?? null;

  useEffect(() => {
    if (!controllerIdentity) {
      setControllerIdentityProfile(null);
      return;
    }
    let cancelled = false;
    fetchPublicProfile(controllerIdentity.handle)
      .then((response) => { if (!cancelled) setControllerIdentityProfile(response.profile); })
      .catch(() => { if (!cancelled) setControllerIdentityProfile(null); });
    return () => { cancelled = true; };
  }, [controllerIdentity?.handle]);

  const label = data?.profile.displayHandle || controllerIdentityProfile?.displayName ||
    controllerIdentity?.handle.replace(/^[^:]+:/, "") ||
    (controllerAddress ? `Wallet ${shortAddr(controllerAddress)}` : "Public x402 wallet profile");

  return (
    <>
      <Nav current="x402" />
      <main className="overflow-x-clip">
        <section className="px-6 md:px-10 pt-10 md:pt-16 max-w-6xl mx-auto">
          <Link to="/x402" className="btn-mono text-xs text-muted hover:text-ink">← Recognized endpoints</Link>
          <header className="mt-7 max-w-4xl">
            <div className="flex flex-wrap items-center gap-3">
              <p className="btn-mono text-xs uppercase tracking-[0.18em] text-muted">x402 wallet profile</p>
              {data && <span className="pill pill-olive btn-mono text-[10px]">Public profile</span>}
            </div>
            <div className="mt-3 flex min-w-0 items-center gap-4">
              {controllerIdentityProfile?.avatarUrl && (
                <img src={controllerIdentityProfile.avatarUrl} alt="" className="h-14 w-14 shrink-0 rounded-full border border-faint object-cover md:h-16 md:w-16" />
              )}
              <div className="min-w-0">
                <h1 className="text-4xl md:text-6xl font-display tracking-tight leading-[0.98] break-words">{label}</h1>
                {controllerIdentity && (
                  <Link className="mt-2 inline-flex btn-mono text-xs text-olive-deep hover:underline" to={`/p/${encodeURIComponent(controllerIdentity.handle)}`}>
                    {controllerIdentity.handle} profile →
                  </Link>
                )}
              </div>
            </div>
            <p className="mt-5 text-base text-muted leading-relaxed max-w-3xl">
              Separately controlled wallets and evidence-labeled x402 activity. Linking proves control and consent, never shared spending authority or a trust score.
            </p>
          </header>
        </section>

        <section className="px-6 md:px-10 mt-10 max-w-6xl mx-auto">
          {loading && <Panel title="Reading the public wallet profile…" />}
          {!loading && error && <Panel title="Could not load this public profile." detail={error} />}
          {!loading && data && (
            <>
              <section className="border-y border-faint py-8" aria-labelledby="public-wallets-heading">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Verified membership</p>
                    <h2 id="public-wallets-heading" className="mt-2 text-3xl font-display">Linked wallets</h2>
                  </div>
                  <p className="btn-mono text-xs text-muted">Wallet set {data.profile.walletSetVersion} · {data.profile.wallets.length} active</p>
                </div>
                <ul className="mt-6 divide-y divide-faint border-y border-faint" aria-label="Public linked wallets">
                  {data.profile.wallets.map((wallet) => {
                    const address = baseAddressFromAccountId(wallet.accountId);
                    return (
                      <li key={wallet.accountId} className="py-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          {address ? (
                            <Link
                              className="btn-mono text-sm text-olive-deep hover:underline"
                              to={`/x402/reviewers/${address}`}
                              title={wallet.accountId}
                              aria-label={`${wallet.isController ? "Controller" : "Member"} wallet ${wallet.accountId}`}
                            >
                              {shortAddr(address)}
                            </Link>
                          ) : (
                            <p className="btn-mono text-sm break-all">{wallet.accountId}</p>
                          )}
                          <p className="mt-1 text-xs text-muted">{wallet.isController ? "Controller" : "Member"} · Base · linked {formatDate(wallet.linkedAt)}</p>
                          <X402ProfileIdentityLinks identities={identitiesByAccount[wallet.accountId] ?? []} />
                        </div>
                        <span className="pill pill-faint btn-mono text-[10px] shrink-0">{wallet.proofKind.toUpperCase()} control proof</span>
                      </li>
                    );
                  })}
                </ul>
                {identityLookupFailed && (
                  <p className="mt-3 text-xs text-muted">Some linked identities are temporarily unavailable. Wallet proofs and activity remain unchanged.</p>
                )}
              </section>

              <section className="py-8" aria-labelledby="profile-activity-heading">
                <div className="max-w-3xl">
                  <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Published facts</p>
                  <h2 id="profile-activity-heading" className="mt-2 text-3xl font-display">Recent x402 activity</h2>
                  <details className="mt-3 text-sm text-muted">
                    <summary className="cursor-pointer min-h-11 inline-flex items-center btn-mono text-xs text-olive-deep">How evidence is labeled</summary>
                    <p className="pb-2 leading-relaxed">
                      Provider receipts bind use to a route. Wallet attestations pair a stated route with a verified Base settlement. A transfer without route context stays labeled only as a settlement.
                    </p>
                  </details>
                  {data.pagination?.hasMore && (
                    <p className="mt-2 btn-mono text-xs text-muted">Showing the latest {data.activity?.length ?? 0} public facts</p>
                  )}
                </div>
                {data.activity !== null && (
                  <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filter recent x402 activity">
                    {FILTERS.map((entry) => {
                      const selected = filter === entry.value;
                      return (
                        <button
                          key={entry.value}
                          type="button"
                          aria-pressed={selected}
                          className={`btn btn-mono ${selected ? "btn-primary" : "btn-ghost"}`}
                          onClick={() => setFilter(entry.value)}
                        >
                          {entry.label}{data.pagination?.hasMore ? "" : ` ${countProfileActivityKind(data.activity ?? [], entry.value)}`}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="mt-6" aria-live="polite">
                  {data.activity === null ? (
                    <Panel title="This older profile response did not include recent activity." detail="Refresh shortly. Public profiles now include their newest published facts." />
                  ) : data.activity.length === 0 ? (
                    <Panel title="No x402 activity has been published yet." detail="New exact-price Base calls made with boon x402 fetch publish automatically. Existing onchain transfers cannot identify an HTTP route by themselves." />
                  ) : activity.length === 0 ? (
                    <Panel title="No activity facts match this filter." />
                  ) : (
                    <ol className="divide-y divide-faint border-y border-faint">
                      {activity.map((fact) => <X402ActivityRow key={fact.id} fact={fact} />)}
                    </ol>
                  )}
                </div>
              </section>

              <details className="border-y border-faint py-5 text-sm text-muted leading-relaxed">
                <summary className="cursor-pointer min-h-11 inline-flex items-center btn-mono text-xs text-olive-deep">About public copies</summary>
                <p className="pb-2">Turning discovery off removes future Boon profile responses but cannot erase onchain records or copies already returned by third parties.</p>
              </details>
            </>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}

function Panel({ title, detail }: { title: string; detail?: string }) {
  return <div className="border-y border-faint py-12 text-center"><p className="font-display text-2xl">{title}</p>{detail && <p className="mt-2 text-sm text-muted">{detail}</p>}</div>;
}

export function profileActivityMatchesFilter(fact: X402ProfileActivity, filter: ActivityFilter): boolean {
  if (filter === "all") return true;
  if (filter === "reviewed") return fact.kind === "reviewed" || hasAttachedReview(fact);
  return fact.kind === filter;
}

export function countProfileActivityKind(activity: X402ProfileActivity[], filter: ActivityFilter): number {
  return filter === "all" ? activity.length : activity.filter((fact) => profileActivityMatchesFilter(fact, filter)).length;
}

function hasAttachedReview(fact: X402ProfileActivity): boolean {
  if (fact.details === null || typeof fact.details !== "object" || Array.isArray(fact.details)) return false;
  const review = (fact.details as Record<string, unknown>).review;
  return review !== null && typeof review === "object" && !Array.isArray(review);
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1_000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
